import React, { FC } from 'react';
import { Box, Typography, Button, useMediaQuery } from '@mui/material';
import { ChatBubbleOutline } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { formatShortFriendlyDateWithTime } from '../../../../helpers';
import { IBuildMessage } from '../../../../models';
import ReactPlayer from 'react-player';

interface IMessagePreviewProps {
  message?: IBuildMessage;
}

export const MessagePreview: FC<IMessagePreviewProps> = ({ message }) => {
  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 900px)');

  if (!message) {
    return (
      <Box className={classes.previewContainer}>
        <Box className={classes.emptyStateContainer}>
          <ChatBubbleOutline className={classes.emptyStateIcon} />
          <Typography variant="body2" color="textSecondary">
            Select a message to view
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.previewContainer}>
      <Box className={classes.messageHeader}>
        <Typography variant="body1" fontWeight="bold">
          {message.createdByFullName || message.createdBy} -{' '}
          {formatShortFriendlyDateWithTime(message.createdAt)}
        </Typography>
      </Box>

      <Box className={classes.messageContent}>
        <Box className={classes.messageTextContainer}>
          <Typography variant="body1" className={classes.messageText}>
            {message.message}
          </Typography>
        </Box>

        {/* Image */}
        {message.fileType === 'Image' && message.fileUrl && (
          <Box className={classes.imageContainer}>
            <img src={message.fileUrl} alt="Message attachment" className={classes.attachedImage} />
          </Box>
        )}

        {/* Video */}
        {message.fileType === 'Video' && message.fileUrl && (
          <Box sx={{ width: '180px', minWidth: '120px' }}>
            <ReactPlayer
              url={message.fileUrl}
              controls
              width="100%"
              height="100%"
              onReady={e => {
                setTimeout(() => {
                  const player = e?.getInternalPlayer();
                  player?.setAttribute?.('muted', 'false');
                }, 200);
              }}
            />
          </Box>
        )}

        {/* PDF */}
        {message.fileType === 'PDF' && message.fileUrl && (
          <Box sx={{ width: isMobile ? '100%' : '180px', minWidth: '120px' }}>
            {isMobile ? (
              <Button
                component="a"
                href={message.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                size="small"
              >
                View PDF
              </Button>
            ) : (
              <iframe src={message.fileUrl} title="PDF Preview" width="100%" height="250"></iframe>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  previewContainer: {
    height: '325px',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      height: '200px'
    }
  },
  emptyStateContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    minHeight: '200px'
  },
  emptyStateIcon: {
    fontSize: '3rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(1)
  },
  messageHeader: {
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[200]}`
  },
  messageContent: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start'
  },
  messageTextContainer: {
    flex: 1,
    minWidth: 0
  },
  messageText: {
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap'
  },
  imageContainer: {
    flexShrink: 0,
    width: '100px',
    height: '100px'
  },
  attachedImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`
  }
}));
